import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { SubcategoryTable, SubcategoryFilter, Pagination, SubcategoryFormModal } from '../components';
import { fetchAllSubcategories, createSubcategory, createBulkSubcategories } from '../services/api';

const SubcategoryManagement = () => {
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [filters, setFilters] = useState({
    categoryId: '',
    middleCategoryId: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  const fetchSubcategoriesData = async (page = currentPage) => {
    console.log('=== FETCH SUBCATEGORIES DATA ===');
    console.log('Current filters:', filters);
    console.log('Current page:', page);
    
    setLoading(true);
    try {
      const params = {
        ...filters,
        page,
        size: 10,
        sort: 'id,desc'
      };
      
      console.log('API call params:', params);
      const data = await fetchAllSubcategories(params);
      setSubcategories(data.content || data || []);
      
      // Handle both paginated and non-paginated responses
      if (data.totalPages !== undefined) {
        setTotalPages(data.totalPages || 0);
        setCurrentPage(data.page || 0);
      } else {
        setTotalPages(1);
        setCurrentPage(0);
      }
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      toast.error('Failed to fetch subcategories. Please try again.');
      setSubcategories([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('=== FILTERS CHANGED - USEEFFECT TRIGGERED ===');
    console.log('New filters:', filters);
    
    // Only fetch if we have a categoryId (avoid fetching with empty filters)
    if (filters.categoryId) {
      console.log('CategoryId exists, fetching data...');
      fetchSubcategoriesData();
    } else {
      console.log('No categoryId, skipping fetch');
      setSubcategories([]);
    }
  }, [filters]);

  const handlePageChange = (newPage) => {
    if (newPage !== currentPage) {
      fetchSubcategoriesData(newPage);
    }
  };

  const handleFilterChange = (newFilters) => {
    console.log('=== HANDLE FILTER CHANGE ===');
    console.log('New filters received:', newFilters);
    console.log('Old filters:', filters);
    
    // Create a clean filter object
    const cleanFilters = {
      categoryId: newFilters.categoryId || '',
      middleCategoryId: newFilters.middleCategoryId || ''
    };
    
    console.log('Clean filters to set:', cleanFilters);
    setFilters(cleanFilters);
    setCurrentPage(0);
    
    console.log('Filters updated, useEffect should trigger');
  };

  const handleCreateSubcategory = async (subcategoryData) => {
    setLoading(true);
    try {
      await createSubcategory(subcategoryData);
      toast.success('Subcategory created successfully!');
      setShowCreateModal(false);
      fetchSubcategoriesData(0); // Refresh and go to first page
    } catch (error) {
      console.error('Error creating subcategory:', error);
      toast.error('Failed to create subcategory. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreateSubcategories = async (bulkData) => {
    setLoading(true);
    try {
      await createBulkSubcategories(bulkData);
      const count = bulkData.subcategories.length;
      toast.success(`${count} subcategories created successfully!`);
      setShowCreateModal(false);
      fetchSubcategoriesData(0); // Refresh and go to first page
    } catch (error) {
      console.error('Error creating bulk subcategories:', error);
      toast.error('Failed to create subcategories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Subcategory Management</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Add Subcategory
        </button>
      </div>

      <SubcategoryFilter onFilterChange={handleFilterChange} initialFilters={filters} />

      <SubcategoryTable 
        subcategories={subcategories} 
        loading={loading}
        onRefresh={fetchSubcategoriesData}
      />

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {showCreateModal && (
        <SubcategoryFormModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateSubcategory}
          onBulkSave={handleBulkCreateSubcategories}
        />
      )}
    </div>
  );
};

export default SubcategoryManagement; 